import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Table as AntdTable, Form, TablePaginationConfig, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import Modal from 'components/Modal';
import { CALENDAR_VIEW_TYPE } from 'features/appointment/services/constants';
import { formatPhone } from 'utils/formatPhone';
import styled from 'styled-components';
import DateViewControls from 'components/DateViewControls';
import checkin_svg from './svgs/checkin.svg';
import walkin_svg from './svgs/walkin.svg';
import calendar_svg from './svgs/calendar.svg';
import calendar_table_svg from './svgs/calendar_table.svg';
import walkin_table_svg from './svgs/walkin_table.svg';
import { useAppDispatch } from 'store/hooks';
import userActions from 'features/user/services/actions';
import userSelectors from 'features/user/services/selectors';
import { ICheckInType, IHistoryCheckInResData } from 'features/user/services/types/clock-in-out';
import Button from 'components/Button';
import SearchIcon from 'assets/Icons/search';
import { IApiCheckInHistoriesParams, IApiCheckInHistoriesSummaryParams } from 'features/user/services/types/api';
import { v4 as uuidv4 } from 'uuid';
import Box from 'components/Box';
import Lock from 'assets/Icons/lock';
import UnLock from 'assets/Icons/unlock';
import RequestAdminLocalPW from 'widgets/RequestAdminLocalPW';
import Text from 'components/Text';

export type OpenHistoryCheckInModalProps = Record<string, never>;

type HistoryCheckInModalRef = {
  open: (data?: OpenHistoryCheckInModalProps) => Promise<void>;
};

type FormValues = {
  viewType: CALENDAR_VIEW_TYPE;
  timePeriod: { startTime: Dayjs; endTime: Dayjs };
  keySearch: string;
};

const initialValues = {
  viewType: CALENDAR_VIEW_TYPE.DAY_VIEW,
  timePeriod: { startTime: dayjs(), endTime: dayjs() },
  keySearch: '',
};

export const useHistoryCheckInModalRef = () => useRef<HistoryCheckInModalRef>(null);
const HISTORY_CHECKIN_SIZE = 10;

const HistoryCheckInModal = forwardRef<HistoryCheckInModalRef>((_, ref) => {
  const dispatch = useAppDispatch();
  const [visible, setVisible] = useState(false);
  const callBackRef = useRef<((value: void) => void) | null>(null);
  const checkInHistories = userSelectors.getCheckInHistories();
  const checkInHistoriesSummary = userSelectors.getCheckInHistoriesSummary();
  const [form] = Form.useForm<FormValues>();
  const [isMaskPhone, setMaskPhone] = useState(true);
  const [visiblePW, setVisiblePW] = useState(false);

  const maskPhone = (phone: string) => {
    const formatted = formatPhone(phone);

    const prefix = formatted?.slice(0, formatted.indexOf(')') + 1);
    const last3 = formatted?.replace(/\D/g, '').slice(-3);

    return `${prefix} ****${last3}`;
  };
  
  const handleToggleVerify = () => {
    const nextToggle = !isMaskPhone;
    if (!nextToggle) return setVisiblePW(true);
    setMaskPhone(true);
  };

  const onVerifyAdminSuccess = () => {
    setMaskPhone(false);
    setVisiblePW(false);
  };

  const handleClose = () => {
    setVisible(false);
    callBackRef?.current?.();
    form.resetFields();
    setMaskPhone(true);
  };

  useImperativeHandle(ref, () => ({
    open: async () => {
      const params: IApiCheckInHistoriesParams = {
        page: 1,
        size: HISTORY_CHECKIN_SIZE,
        startDate: dayjs().format('MM-DD-YYYY'),
        endDate: dayjs().format('MM-DD-YYYY'),
        keySearch: '',
      };
      const paramsSummary: IApiCheckInHistoriesSummaryParams = {
        startDate: dayjs().format('MM-DD-YYYY'),
        endDate: dayjs().format('MM-DD-YYYY'),
      };
      dispatch(userActions.getCheckInHistories.fetch(params));
      dispatch(userActions.getCheckInHistoriesSummary.fetch(paramsSummary));
      setVisible(true);
      return new Promise<void>((resolve) => {
        callBackRef.current = resolve;
      });
    },
  }));

  const listenCheckInHistories = useMemo(() => {
    const list = checkInHistories?.content ? checkInHistories?.content?.map((item: IHistoryCheckInResData) => ({
      ...item,
      id: uuidv4() as string,
    })) : [];
    return list;
  }, [checkInHistories?.content]);

  if (!visible) return null;

  const columns: ColumnsType<IHistoryCheckInResData> = [
    {
      title: 'Tickets',
      dataIndex: 'ticketNumber',
      key: 'ticketNumber',
      align: 'left',
      render: (ticketNumber: number) => <TicketBox>#{ticketNumber}</TicketBox>,
    },
    {
      title: 'Customers Name',
      dataIndex: 'customerName',
      key: 'customerName',
      align: 'left',
    },
    {
      title: (
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          Customer Phone
          <LockButton onClick={handleToggleVerify}>
            {isMaskPhone ? <UnLock /> : <Lock />}
          </LockButton>
        </Box>
      ),
      dataIndex: 'customerPhone',
      key: 'customerPhone',
      align: 'left',
      width: 300,
      render: (customerPhone: string) => (
        <Text variant='BODY_1' color='text_3' textAlign='left'>
          {isMaskPhone ? maskPhone(customerPhone) : formatPhone(customerPhone)}
        </Text>
      ),
    },
    {
      title: 'Date & Time',
      dataIndex: 'checkInTime',
      key: 'checkInTime',
      align: 'left',
      render: (checkInTime: string) => dayjs(checkInTime).format('MM-DD-YYYY hh:mmA'),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      align: 'left',
      render: (type: ICheckInType) => (
        <TypeBadge className={type === ICheckInType.APPOINTMENT ? 'appointment' : 'walkin'}>
          {type === ICheckInType.APPOINTMENT ? (
            <img src={calendar_table_svg} alt='calendar' style={{ width: 24, height: 24 }} />
          ) : (
            <img src={walkin_table_svg} alt='clock' style={{ width: 24, height: 24 }} />
          )}
          <span>{type === ICheckInType.APPOINTMENT ? 'Appointment' : 'Walk-in'}</span>
        </TypeBadge>
      ),
    },
  ];

  const convertStartDateBaseOnViewType = (viewType: CALENDAR_VIEW_TYPE, startTime: Dayjs) => {
    if (viewType === CALENDAR_VIEW_TYPE.MONTH_VIEW) {
      return startTime.startOf('month').format('MM-DD-YYYY');
    } else if (viewType === CALENDAR_VIEW_TYPE.WEEK_VIEW) {
      return startTime.startOf('week').format('MM-DD-YYYY');
    } else if (viewType === CALENDAR_VIEW_TYPE.TWO_WEEKS) {
      return startTime.startOf('week').format('MM-DD-YYYY');
    }
    return startTime.format('MM-DD-YYYY');
  };

  const convertEndDateBaseOnViewType = (viewType: CALENDAR_VIEW_TYPE, endTime: Dayjs) => {
    if (viewType === CALENDAR_VIEW_TYPE.MONTH_VIEW) {
      return endTime.endOf('month').format('MM-DD-YYYY');
    } else if (viewType === CALENDAR_VIEW_TYPE.WEEK_VIEW) {
      return endTime.endOf('week').format('MM-DD-YYYY');
    } else if (viewType === CALENDAR_VIEW_TYPE.TWO_WEEKS) {
      return endTime.endOf('week').add(1, 'week').format('MM-DD-YYYY');
    }
    return endTime.format('MM-DD-YYYY');
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (pagination.current) {
      const formValues = form.getFieldsValue();
      const params = {
        page: pagination.current,
        startDate: convertStartDateBaseOnViewType(formValues.viewType, formValues.timePeriod.startTime),
        endDate: convertEndDateBaseOnViewType(formValues.viewType, formValues.timePeriod.endTime),
        keySearch: formValues.keySearch,
        size: HISTORY_CHECKIN_SIZE,
      };
      dispatch(userActions.getCheckInHistories.fetch(params));
    }
  };

  const handleFinish = (values: FormValues) => {
    const startDateBaseOnViewType = convertStartDateBaseOnViewType(values.viewType, values.timePeriod.startTime);
    const endDateBaseOnViewType = convertEndDateBaseOnViewType(values.viewType, values.timePeriod.endTime);
    const params = {
      startDate: startDateBaseOnViewType,
      endDate: endDateBaseOnViewType,
      keySearch: values.keySearch,
      page: 1,
      size: HISTORY_CHECKIN_SIZE,
    };
    const paramsSummary = {
      startDate: startDateBaseOnViewType,
      endDate: endDateBaseOnViewType,
    };
    dispatch(userActions.getCheckInHistories.fetch(params));
    dispatch(userActions.getCheckInHistoriesSummary.fetch(paramsSummary));
  };

  const SummaryCardComponent = ({ label, value, icon }: { label: string, value: number, icon: string }) => {
    return (
      <SummaryCard>
        <div>
          <div className="label">{label}</div>
          <div className="value">{value}</div>
        </div>
        <div>
          <img src={icon} alt={label} />
        </div>
      </SummaryCard>
    );
  };

  return (
    <Modal
      modalTitle="HISTORY CHECK IN"
      visible={visible}
      onClose={handleClose}
      hiddenOkButton
      cancelTitle="Close"
      v2
      width={1200}
      noneBodyStyle
      destroyOnClose
      wrapClassName="history-check-in-modal"
    >
      <Container>
        <Form form={form} onFinish={handleFinish} initialValues={initialValues}>
          <FilterRow>
            <DateViewControls />
            <Form.Item name={'keySearch'} noStyle>
              <Input
                size='large'
                suffix={<SearchIcon />}
                placeholder="Customer name/phone"
              />
            </Form.Item>
            <Button ntype='PRIMARY' htmlType='submit'>Search</Button>
          </FilterRow>
        </Form>

        <SummaryRow>
          <SummaryCardComponent label="Total Check In" value={checkInHistoriesSummary?.totalCheckIn || 0} icon={checkin_svg} />
          <SummaryCardComponent label="Total Walk-In" value={checkInHistoriesSummary?.totalWalkIn || 0} icon={walkin_svg} />
          <SummaryCardComponent label="Total Appointment" value={checkInHistoriesSummary?.totalAppointment || 0} icon={calendar_svg} />
        </SummaryRow>

        <TableWrapper>
          <StyledTable
            columns={columns}
            dataSource={listenCheckInHistories}
            rowKey="id"
            onChange={handleTableChange}
            pagination={{
              total: checkInHistories?.totalElements || 0,
              pageSize: checkInHistories?.pageable?.pageSize || HISTORY_CHECKIN_SIZE,
              current: (checkInHistories?.pageable?.pageNumber ?? 0) + 1,
              showSizeChanger: false,
            }}
          />
        </TableWrapper>
      </Container>
      {visiblePW && (
        <RequestAdminLocalPW
          onlyAdmin
          visible
          v2={true}
          onCorrect={onVerifyAdminSuccess}
          onClose={() => setVisiblePW(false)}
        />
      )}
    </Modal>
  );
});

HistoryCheckInModal.displayName = 'HistoryCheckInModal';
export default HistoryCheckInModal;

const Container = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const SummaryCard = styled.div`
  flex: 1;
  min-width: 160px;
  padding: 16px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e5e6eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  .label {
    color: #505050;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 30px;
    text-transform: capitalize;
  }
  .value {
    color: #1D2129;
    font-family: Poppins;
    font-size: 32px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled(AntdTable<IHistoryCheckInResData>)`
  .ant-table {
    background: transparent;
    height: 723px;
  }

  .ant-table-thead > tr > th {
    background: #6fabb6;
    color: #ffffff;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    border-bottom: none;
    padding: 16px;

    &::before {
      display: none;
    }
  }

  .ant-table-tbody > tr {
    background: #ffffff;

    &:nth-child(even) {
      background: #eef4ff;
    }

    &:hover > td {
      background: #e6f0ff;
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #f0f0f0;
    padding: 16px;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    color: #1d2129;
  }

  .ant-pagination {
    margin: 20px 0;
  }

  .ant-pagination-item-active {
    border-color: #6fabb6;
    a {
      color: #6fabb6;
    }
  }

  .ant-pagination-item:hover {
    border-color: #6fabb6;
    a {
      color: #6fabb6;
    }
  }

  .ant-pagination-prev:hover .ant-pagination-item-link,
  .ant-pagination-next:hover .ant-pagination-item-link {
    color: #6fabb6;
    border-color: #6fabb6;
  }

  .ant-pagination-prev .ant-pagination-item-link,
  .ant-pagination-next .ant-pagination-item-link {
    font-size: 20px;
  }

  .ant-pagination-prev .ant-pagination-item-link .anticon,
  .ant-pagination-next .ant-pagination-item-link .anticon {
    font-size: 20px;
  }
`;

const TicketBox = styled.div`
  display: flex;
  width: fit-content;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  background: #232f3e;
  color: #fff;
  font-family: Poppins;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`;

const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-family: Poppins;
  font-size: 14px;
  font-weight: 500;
  border-radius: 5px;
  font-family: Poppins;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  &.appointment {
    background: #E4F4F6;
    color: #2D6772;
  }
  &.walkin {
    background: #FFF1F6;
    color: #D2464F;
  }
`;

const LockButton = styled.button`
  background: transparent;
  border: none;
`;